import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Login from '../pages/auth/login';
import ResetPasswordRequest from '../pages/auth/reset-password-request';
import ResetPassword from '../pages/auth/reset-password';
import Layout from '../layout';
import HomePage from '../page';
import Users from '../pages/users/index';
import Organizations from '../pages/organizations/index';
import Compliance from '../pages/compliance/index';
import CopyPaste from '../pages/compliance/copy-paste';
import FileUpload from '../pages/compliance/file-upload';
import FormPage from '../pages/compliance/form';
import PendingPage from '../pages/compliance/pending';
import Rules from '../pages/rules/index';
import NewUser from '../pages/users/new';
import UserForm from '../pages/users/new';
import UserDetails from '../pages/users/details';
import RuleForm from '../pages/rules/new';
import RuleDetails from '../pages/rules/details';
import MfaVerification from '../pages/auth/mfa/index';
import OrganizationForm from '../pages/organizations/new';
import OrganizationDetails from '../pages/organizations/details';
import { FiEdit, FiEye } from "react-icons/fi";
import Results from '../pages/results/index';
import AdminResults from '../pages/results/admin';
import Account from '../pages/account';
import Folders from '../pages/folders/index';
import FolderDetails from '../pages/folders/details/index';

// Componente que verifica autenticação
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente para rotas que requerem privilégios de admin
const AdminOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, hasAdminAccess } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAdminAccess()) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente para rotas que requerem privilégios de admin ou manager
const AdminOrManagerRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, hasAdminAccess, isManager } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAdminAccess() && !isManager()) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Componente para rotas com header mas sem sidebar
const HeaderOnlyRoute: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Componente que renderiza diferentes componentes com base no perfil do usuário
const ProfileBasedRoute: React.FC<{adminComponent: React.ReactNode, userComponent: React.ReactNode}> = ({
  adminComponent,
  userComponent
}) => {
  const { hasAdminAccess } = useAuth();
  
  return (
    <PrivateRoute>
      {hasAdminAccess() ? adminComponent : userComponent}
    </PrivateRoute>
  );
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <BrowserRouter>
      <Routes>
        {/* Rotas públicas (não protegidas) */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <Login />
          } 
        />
        
        <Route 
          path="/reset-password-request" 
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordRequest />
          } 
        />
        
        <Route 
          path="/reset-password" 
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <ResetPassword />
          } 
        />
        
        <Route 
          path="/reset-password/:token" 
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <ResetPassword />
          } 
        />
        
        {/* Rotas protegidas */}
        <Route path="/" element={
          <PrivateRoute>
            <HomePage />
          </PrivateRoute>
        } />
        
        {/* Rotas de acesso para admin ou manager */}
        <Route path="/users" element={
          <AdminOrManagerRoute>
            <Users />
          </AdminOrManagerRoute>
        } />
        
        <Route path="/organizations" element={
          <AdminOnlyRoute>
            <Organizations />
          </AdminOnlyRoute>
        } />
        
        <Route path="/compliance" element={
          <PrivateRoute>
            <Compliance />
          </PrivateRoute>
        } />

        <Route path="/compliance/copy-paste" element={
          <PrivateRoute>
            <CopyPaste />
          </PrivateRoute>
        } />

        <Route path="/compliance/file-upload" element={
          <PrivateRoute>
            <FileUpload />
          </PrivateRoute>
        } />

        <Route path="/compliance/form" element={
          <PrivateRoute>
            <FormPage />
          </PrivateRoute>
        } />

        <Route path="/compliance/pending" element={
          <PrivateRoute>
            <PendingPage />
          </PrivateRoute>
        } />

        <Route path="/compliance/results" element={
          <ProfileBasedRoute 
            adminComponent={<AdminResults />} 
            userComponent={<Results />}
          />
        } />

        <Route path="/rules" element={
          <PrivateRoute>
            <Rules />
          </PrivateRoute>
        } />
        
        <Route path="/users/new" element={
          <AdminOrManagerRoute>
            <UserForm />
          </AdminOrManagerRoute>
        } />
        
        <Route path="/users/edit/:id" element={
          <AdminOrManagerRoute>
            <UserForm />
          </AdminOrManagerRoute>
        } />

        <Route path="/organizations/new" element={
          <AdminOnlyRoute>
            <OrganizationForm />
          </AdminOnlyRoute>
        } />

        <Route path="/organizations/edit/:id" element={
          <AdminOnlyRoute>
            <OrganizationForm />
          </AdminOnlyRoute>
        } />  
        
        <Route path="/organizations/details/:id" element={
          <AdminOnlyRoute>
            <OrganizationDetails />
          </AdminOnlyRoute>
        } />
        
        <Route path="/users/details/:id" element={
          <AdminOrManagerRoute>
            <UserDetails />
          </AdminOrManagerRoute>
        } />

        <Route path="/results" element={
          <ProfileBasedRoute 
            adminComponent={<AdminResults />} 
            userComponent={<Results />}
          />
        } />
        
        {/* Novas rotas para Rules */}
        <Route path="/rules/new" element={
          <PrivateRoute>
            <RuleForm />
          </PrivateRoute>
        } />
        
        <Route path="/rules/edit/:id" element={
          <PrivateRoute>
            <RuleForm />
          </PrivateRoute>
        } />
        
        <Route path="/rules/details/:id" element={
          <PrivateRoute>
            <RuleDetails />
          </PrivateRoute>
        } />

        {/* Rotas para Pastas */}
        <Route path="/folders" element={
          <PrivateRoute>
            <Folders />
          </PrivateRoute>
        } />
        
        <Route path="/folders/:id" element={
          <PrivateRoute>
            <FolderDetails />
          </PrivateRoute>
        } />

        {/* Rota para a página da conta */}
        <Route path="/account" element={
          <HeaderOnlyRoute>
            <Account />
          </HeaderOnlyRoute>
        } />

        {/* Rota para páginas não encontradas */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRoutes;